import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ehContaDeEquipe } from '@/lib/equipeLink';
import { adiarCadastro, cadastroAdiado } from '@/lib/faixaApp';
import {
  mascaraCPF,
  mascaraTelefone,
  validarCPF,
} from '@/pages/inscricao/utils';
import CampoTexto from '@/components/base/CampoTexto';
import Logo from '@/components/ui/Logo';

// ---------------------------------------------------------------------------
// Cadastro obrigatório: nome completo, CPF (com dígitos verificadores) e
// WhatsApp com DDD. Vale no celular e no computador. Quem está com o cadastro
// incompleto vê esta tela e só sai dela salvando. Contas de equipe (função) e
// as rotas de conta/login ficam de fora.
// ---------------------------------------------------------------------------

// Fluxos públicos (inscrição, compra, talão): aqui sempre existe "Preencher depois".
const ROTAS_COM_DEPOIS = [
  '/e/',
  '/inscricao',
  '/minha-inscricao',
  '/minhas-inscricoes',
  '/ingresso',
  '/pedido',
  '/meu-talao',
  '/t/',
  '/buscar-talao',
  '/loja',
  '/carteira',
];

const ROTAS_ISENTAS = [
  '/entrar',
  '/cadastro',
  '/recuperar-senha',
  '/nova-senha',
  '/excluir-conta',
  '/termos',
  '/privacidade',
  '/instalar',
  '/criancas',
];

interface Rascunho {
  nome: string;
  cpf: string;
  telefone: string;
}

type Faltando = 'nome' | 'cpf' | 'whatsapp';

function soDigitos(v: string | null | undefined): string {
  return (v ?? '').replace(/\D/g, '');
}

export default function PortaoCadastro() {
  const { pathname } = useLocation();
  const isenta = ROTAS_ISENTAS.some((r) => pathname === r || pathname.startsWith(r + '/'));

  const [checando, setChecando] = useState(true);
  const [precisa, setPrecisa] = useState(false);
  const [faltando, setFaltando] = useState<Faltando[]>([]);
  const [rascunho, setRascunho] = useState<Rascunho>({ nome: '', cpf: '', telefone: '' });
  const [erros, setErros] = useState<Partial<Record<Faltando, string>>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [pulado, setPulado] = useState(() => cadastroAdiado());

  // Na inscrição/compra a pessoa pode preencher depois — nunca trava o fluxo.
  const podePular = ROTAS_COM_DEPOIS.some(
    (r) => pathname === r.replace(/\/$/, '') || pathname.startsWith(r),
  );

  const verificar = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user || ehContaDeEquipe(user)) {
      setPrecisa(false);
      setChecando(false);
      return;
    }

    // Caminho oficial: rpc meu_cadastro_completo (pode ainda não existir).
    const { data: rpcData, error: rpcErr } = await supabase.rpc('meu_cadastro_completo');
    if (!rpcErr && rpcData && typeof rpcData === 'object') {
      const d = rpcData as { completo?: boolean; falta?: string[] };
      const falta = (d.falta ?? []).filter((f): f is Faltando =>
        f === 'nome' || f === 'cpf' || f === 'whatsapp',
      );
      setFaltando(falta);
      setPrecisa(d.completo === false || falta.length > 0);
      await preencher(user.id);
      setChecando(false);
      return;
    }

    // Fallback: lê a própria linha em `usuarios` e valida no navegador.
    const { data: usu } = await supabase
      .from('usuarios')
      .select('nome, cpf, telefone')
      .eq('id', user.id)
      .maybeSingle();

    const nome = (usu?.nome ?? '').trim();
    const cpf = usu?.cpf ?? '';
    const tel = usu?.telefone ?? '';
    const falta: Faltando[] = [];
    if (!nome) falta.push('nome');
    if (!validarCPF(cpf)) falta.push('cpf');
    if (soDigitos(tel).length < 10) falta.push('whatsapp');

    setFaltando(falta);
    setPrecisa(falta.length > 0);
    setRascunho({
      nome,
      cpf: cpf ? mascaraCPF(cpf) : '',
      telefone: tel ? mascaraTelefone(tel) : '',
    });
    setChecando(false);
  }, []);

  async function preencher(uid: string) {
    const { data: usu } = await supabase
      .from('usuarios')
      .select('nome, cpf, telefone')
      .eq('id', uid)
      .maybeSingle();
    setRascunho({
      nome: (usu?.nome ?? '').trim(),
      cpf: usu?.cpf ? mascaraCPF(usu.cpf) : '',
      telefone: usu?.telefone ? mascaraTelefone(usu.telefone) : '',
    });
  }

  useEffect(() => {
    void verificar();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      setTimeout(() => void verificar(), 0);
    });
    return () => sub.subscription.unsubscribe();
  }, [verificar]);

  if (isenta) return null;
  if (checando || !precisa) return null;
  if (pulado && podePular) return null;

  function preencherDepois() {
    adiarCadastro();
    setPulado(true);
  }

  function mudar(campo: keyof Rascunho, valor: string) {
    let v = valor;
    if (campo === 'cpf') v = mascaraCPF(valor);
    if (campo === 'telefone') v = mascaraTelefone(valor);
    setRascunho((r) => ({ ...r, [campo]: v }));
    setErros((e) => ({ ...e, [campo]: undefined }));
  }

  async function salvar() {
    const mostraNome = faltando.includes('nome') || !rascunho.nome.trim();
    const mostraCpf = faltando.includes('cpf') || !validarCPF(rascunho.cpf);
    const mostraTel = faltando.includes('whatsapp');

    const novos: Partial<Record<Faltando, string>> = {};
    if (mostraNome && !rascunho.nome.trim()) novos.nome = 'Digite seu nome completo.';
    if (mostraCpf && !validarCPF(rascunho.cpf)) novos.cpf = 'Digite um CPF válido.';
    if (mostraTel && soDigitos(rascunho.telefone).length < 10)
      novos.whatsapp = 'Digite o WhatsApp com DDD.';

    setErros(novos);
    setErroGeral(null);
    if (Object.keys(novos).length > 0) return;

    setSalvando(true);
    try {
      const args: Record<string, unknown> = {
        p_nome: rascunho.nome.trim(),
        p_cpf: soDigitos(rascunho.cpf),
        p_telefone: soDigitos(rascunho.telefone),
      };
      const { error } = await supabase.rpc('salvar_meu_perfil', args);
      if (error) {
        setErroGeral('Não conseguimos salvar seus dados. Tente de novo em instantes.');
        return;
      }
      setPrecisa(false);
    } catch {
      setErroGeral('Não conseguimos salvar seus dados. Tente de novo em instantes.');
    } finally {
      setSalvando(false);
    }
  }

  const nomeInvalido = !rascunho.nome.trim();
  const cpfInvalido = !validarCPF(rascunho.cpf);

  return (
    <div className="fixed inset-0 z-[96] overflow-y-auto bg-primary-50 px-4 py-8">
      <div className="mx-auto w-full max-w-[440px]">
        <div className="mb-5 flex justify-center">
          <Logo imgClassName="h-10 w-auto object-contain" />
        </div>

        <div className="rounded-3xl border border-secondary-200 bg-white p-6">
          <h1 className="text-center text-xl font-extrabold text-foreground-950">
            Complete seu cadastro
          </h1>
          <p className="mt-2 text-center text-sm leading-relaxed text-foreground-600">
            Precisamos do seu nome, CPF e WhatsApp para usar o Se Liga. É uma vez só.
          </p>

          <div className="mt-5 space-y-5">
            {(faltando.includes('nome') || nomeInvalido) && (
              <CampoTexto
                id="cad-nome"
                name="nome"
                label="Nome completo"
                value={rascunho.nome}
                onChange={(v) => mudar('nome', v)}
                required
                error={erros.nome ?? null}
                autoComplete="name"
              />
            )}
            {(faltando.includes('cpf') || cpfInvalido) && (
              <CampoTexto
                id="cad-cpf"
                name="cpf"
                label="CPF"
                help="É por ele que identificamos você nos eventos."
                value={rascunho.cpf}
                onChange={(v) => mudar('cpf', v)}
                required
                error={erros.cpf ?? null}
                inputMode="numeric"
                placeholder="000.000.000-00"
                autoComplete="off"
              />
            )}
            {faltando.includes('whatsapp') && (
              <CampoTexto
                id="cad-whatsapp"
                name="telefone"
                label="WhatsApp (com DDD)"
                value={rascunho.telefone}
                onChange={(v) => mudar('telefone', v)}
                required
                error={erros.whatsapp ?? null}
                inputMode="tel"
                placeholder="(00) 00000-0000"
                autoComplete="tel"
              />
            )}
          </div>

          {erroGeral && (
            <p className="mt-4 text-sm text-danger-600" role="alert">
              {erroGeral}
            </p>
          )}

          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="mt-6 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={salvando ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} />
            </span>
            {salvando ? 'Salvando…' : 'Salvar e continuar'}
          </button>

          {podePular && (
            <button
              type="button"
              onClick={preencherDepois}
              className="mt-3 flex h-[48px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl text-sm font-semibold text-foreground-600 transition-colors hover:bg-background-100"
            >
              Preencher depois
            </button>
          )}

          <p className="mt-3 text-center text-xs leading-relaxed text-foreground-500">
            Usamos seus dados só para organizar eventos e emitir ingressos. Veja a{' '}
            <Link to="/privacidade" className="font-semibold text-primary-600 underline">
              política de privacidade
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}